import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProjects, listSurveys, createProject } from "../api";
import type { Project } from "../api";
import { useTitle } from "../hooks/useTitle";

interface ProjectRow {
  project: Project;
  surveyCount: number | null;
}

export default function Dashboard() {
  useTitle("Tableau de bord");
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const projects = await listProjects();
        const active = projects.filter((p) => p.status === "active");
        if (!cancelled) {
          setRows(active.map((project) => ({ project, surveyCount: null })));
          setLoading(false);
        }
        await Promise.all(
          active.map(async (project) => {
            try {
              const surveys = await listSurveys(project.id);
              if (!cancelled)
                setRows((prev) =>
                  prev.map((r) =>
                    r.project.id === project.id
                      ? { ...r, surveyCount: surveys.length }
                      : r
                  )
                );
            } catch {
              if (!cancelled)
                setRows((prev) =>
                  prev.map((r) =>
                    r.project.id === project.id ? { ...r, surveyCount: -1 } : r
                  )
                );
            }
          })
        );
      } catch (e) {
        console.error("Erreur détaillée au chargement des projets:", e);
        if (!cancelled) {
          setError("Impossible de charger les projets.");
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCreate() {
    const name = window.prompt("Nom du projet :");
    if (!name?.trim()) return;
    setCreating(true);
    try {
      const project = await createProject(name.trim());
      setRows((prev) => [...prev, { project, surveyCount: 0 }]);
    } catch (e) {
      alert(`Erreur : ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }

  if (loading) return <p className="status-msg">Chargement…</p>;
  if (error) return <p className="status-msg error">{error}</p>;

  return (
    <main className="dashboard">
      <h1>Projets</h1>
      {rows.length === 0 && <p className="status-msg">Aucun projet actif.</p>}
      <table className="project-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Sondages</th>
            <th>Créé le</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ project, surveyCount }) => (
            <tr key={project.id}>
              <td>
                <Link to={`/projects/${project.id}`}>{project.name}</Link>
              </td>
              <td>
                {surveyCount === null
                  ? "…"
                  : surveyCount === -1
                    ? "erreur"
                    : surveyCount}
              </td>
              <td>{new Date(project.createdAt).toLocaleDateString("fr-CA")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn-create" style={{ marginTop: "1.5rem" }} onClick={handleCreate} disabled={creating}>
        {creating ? "Création…" : "+ Créer un projet"}
      </button>
    </main>
  );
}
